import cn from 'classnames';
import { noop } from 'lodash-es';
import { FC, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { DocumentSectionOverride } from '@/shared/api/transcriptor';
import { Button, Tooltip } from '@/shared/ui';

import {
  normalizeDocumentType,
  type DocumentTemplate,
} from '@/entities/templates';

import { useTemplateIntl, type TemplateEdit } from '../hooks';
import {
  applySectionOverrides,
  getNewSectionTitle,
  withFieldListKeys,
} from '../lib';

import { DroppedTemplateChanges } from './DroppedTemplateChanges';
import styles from './FieldListTemplate.module.scss';
import { DocumentFrame } from './NoteDocument';
import {
  TemplateDocumentEditor,
  type TemplateDocumentControls,
} from './TemplateDocumentEditor';
import { TemplateEditorTools } from './TemplateEditorTools';

type Props = {
  template: DocumentTemplate;
  sourceLabel?: ReactNode;
  // Further tools of the document's toolbar outside the edit mode.
  actions?: ReactNode;
  disabled?: boolean;
  className?: string;
  // On the Templates page, for users who may edit the clinic's templates:
  // the fields edited in place, saved as the clinic's next version.
  edit?: TemplateEdit;
  // Shown once the edit mode is left with changes not saved yet.
  saveBar?: ReactNode;
  // Before a recording: the fields' titles and instructions typed for it
  // alone, as `template`, whose changes are handed on whole.
  prefill?: {
    template?: DocumentTemplate;
    onChange: (template: DocumentTemplate) => void;
  };
  // The titles and instructions a recording was started with.
  sectionOverrides?: DocumentSectionOverride[];
};

const FieldCount: FC<{ count: number }> = ({ count }) => (
  <FormattedMessage
    id="workspace.template.fieldCount"
    defaultMessage="{count, plural, one {# field} other {# fields}}"
    values={{ count }}
  />
);

const DroppedFields: FC<{ edit?: TemplateEdit }> = ({ edit }) =>
  edit && edit.droppedChanges.length > 0 ? (
    <DroppedTemplateChanges
      changes={edit.droppedChanges}
      saved={edit.savedSinceDropped}
      noun="field"
      onHide={edit.hideDroppedChanges}
    />
  ) : null;

type EditFillProps = Pick<
  Props,
  'template' | 'sourceLabel' | 'disabled' | 'className'
> & {
  edit: TemplateEdit;
};

// The fields in the edit mode: the one template editor, where a field is a
// heading with its AI instruction in brackets under it, and no lines.
const FieldListEditFill: FC<EditFillProps> = ({
  template: shown,
  sourceLabel,
  disabled = false,
  className,
  edit,
}) => {
  const { formatMessage } = useIntl();
  const template = edit.draft?.template ?? shown;
  // The document reads the draft once; it writes every change back.
  const [initial] = useState(template);
  const templateIntl = useTemplateIntl(template.language);
  const [controls, setControls] = useState<TemplateDocumentControls>();
  const evaluation = normalizeDocumentType(template.type) === 'EVALUATION';

  const newFieldTitle = getNewSectionTitle(
    template,
    templateIntl.formatMessage({
      id: 'workspace.template.newField',
      defaultMessage: 'New field',
    }),
  );

  return (
    <DocumentFrame
      template={template}
      sourceLabel={sourceLabel}
      size={<FieldCount count={template.sections.length} />}
      disabled={disabled}
      className={className}
      dataComponent="workspace-field-list-document"
      panels={<DroppedFields edit={edit} />}
      history={disabled ? undefined : controls}
      toolbar={
        <>
          {!disabled && <TemplateEditorTools controls={controls} />}
          <Tooltip.Primary
            triggerClassName={styles.toolbarEnd}
            content={formatMessage({
              id: 'workspace.templateEdit.banner',
              defaultMessage:
                'Editing the template — saved as a new clinic version',
            })}
          >
            <Button
              variant="primary"
              size="small"
              // Nothing changed is nothing to save.
              disabled={disabled || !edit.changed}
              data-component="workspace-field-list-done"
              onClick={edit.finish}
            >
              <FormattedMessage id="global.done" defaultMessage="Done" />
            </Button>
          </Tooltip.Primary>
        </>
      }
      lead={
        evaluation && (
          <p
            className={cn('p4', styles.caveat)}
            data-component="workspace-field-list-evaluation-caveat"
          >
            <FormattedMessage
              id="workspace.fieldList.evaluationCaveat"
              defaultMessage="An evaluation's criteria may also come from built-in prompts, so a change here can apply only in part."
            />
          </p>
        )
      }
    >
      <TemplateDocumentEditor
        fields
        template={initial}
        source={edit.source ?? shown}
        lists={[]}
        issues={edit.issues}
        editable={!disabled}
        focus={edit.focus}
        ariaLabel={formatMessage({
          id: 'workspace.fieldList.document',
          defaultMessage: 'Document fields',
        })}
        newSectionTitle={newFieldTitle}
        onChange={edit.change}
        onControls={setControls}
      />
    </DocumentFrame>
  );
};

type PrefillFillProps = Pick<
  Props,
  'template' | 'sourceLabel' | 'actions' | 'disabled' | 'className'
> & {
  prefill: NonNullable<Props['prefill']>;
};

// The fields before a recording: their titles and instructions typed in the
// one template editor for this recording alone; no field is added, removed
// or moved.
const FieldListPrefillFill: FC<PrefillFillProps> = ({
  template: shown,
  sourceLabel,
  actions,
  disabled = false,
  className,
  prefill,
}) => {
  const { formatMessage } = useIntl();
  const source = withFieldListKeys(shown);
  // The document reads the edited fields once; it hands every change on.
  const [initial] = useState(prefill.template ?? source);
  const [controls, setControls] = useState<TemplateDocumentControls>();

  return (
    <DocumentFrame
      template={prefill.template ?? source}
      sourceLabel={sourceLabel}
      size={<FieldCount count={source.sections.length} />}
      disabled={disabled}
      className={className}
      dataComponent="workspace-field-list-document"
      history={disabled ? undefined : controls}
      toolbar={actions}
    >
      <TemplateDocumentEditor
        fields
        structureLocked
        template={initial}
        source={source}
        lists={[]}
        issues={[]}
        editable={!disabled}
        ariaLabel={formatMessage({
          id: 'workspace.fieldList.document',
          defaultMessage: 'Document fields',
        })}
        onChange={({ template }) => prefill.onChange(template)}
        onControls={setControls}
      />
    </DocumentFrame>
  );
};

// A summary or an evaluation as the fields AI writes: each field's title and
// the instruction it is answered by, in the one template editor protocols
// are edited in. A template the clinic may change opens in the edit mode; it
// is read only while its changes wait to be saved or dropped.
export const FieldListTemplate: FC<Props> = ({
  template,
  sourceLabel,
  actions,
  disabled = false,
  className,
  edit,
  saveBar,
  prefill,
  sectionOverrides,
}) => {
  const { formatMessage } = useIntl();
  const opensEditing = !!edit && !edit.editing && !edit.changed && !disabled;

  useEffect(() => {
    if (opensEditing) {
      edit.start();
    }
    // The edit opens once each time the template is shown without one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opensEditing]);

  if (prefill) {
    return (
      <FieldListPrefillFill
        template={template}
        sourceLabel={sourceLabel}
        actions={actions}
        disabled={disabled}
        className={className}
        prefill={prefill}
      />
    );
  }
  if (edit?.editing && edit.draft) {
    return (
      <FieldListEditFill
        // Another source, as after a reload, is another document.
        key={edit.source?.id}
        template={template}
        sourceLabel={sourceLabel}
        disabled={disabled}
        className={className}
        edit={edit}
      />
    );
  }

  const keyed = withFieldListKeys(
    applySectionOverrides(template, sectionOverrides),
  );
  // The editor reads its template once, so another text is another editor.
  const readKey = JSON.stringify(
    keyed.sections.map((section) => [
      section.ID,
      section.Title,
      section.Instructions,
    ]),
  );
  return (
    <DocumentFrame
      template={template}
      sourceLabel={sourceLabel}
      size={<FieldCount count={template.sections.length} />}
      disabled={disabled}
      className={className}
      dataComponent="workspace-field-list-document"
      panels={
        <>
          <DroppedFields edit={edit} />
          {edit?.changed && saveBar}
        </>
      }
      toolbar={actions}
    >
      {keyed.sections.length > 0 ? (
        <TemplateDocumentEditor
          key={readKey}
          fields
          template={keyed}
          source={keyed}
          lists={[]}
          issues={[]}
          editable={false}
          ariaLabel={formatMessage({
            id: 'workspace.fieldList.document',
            defaultMessage: 'Document fields',
          })}
          onChange={noop}
          onControls={noop}
        />
      ) : (
        <p className={cn('p3', styles.empty)}>
          <FormattedMessage
            id="workspace.fieldList.empty"
            defaultMessage="This document has no fields."
          />
        </p>
      )}
    </DocumentFrame>
  );
};
